import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Paper,
  styled,
} from '@mui/material';
import { showExpDate } from '.';
import {
  CsaFilterData,
  CsaRawData,
  SearchResp,
  HeadCell,
  DeaNumFunction,
  DeaPageFunction,
  handleKey,
  StyledTableSortLabel,
  CSA_FILTER_ROWS_PER_PAGE,
  CSA_FILTER_ROWS_PER_PAGE_OPTION,
  IPageInfo,
  StyledTablePagination,
  IDeaEditMode,
  includes,
  FlexBox,
  parseDate,
} from '../common';
import { v4 as uuidv4 } from 'uuid';
import { isEmpty, pathOr } from 'rambda';
import {
  compose as lcompose,
  find as lfind,
  map as lmap,
  filter as lfilter,
  eq as leq,
  negate as lnegate,
} from 'lodash/fp';
import { flow } from 'fp-ts/function';

// DEA Filter table header
const headCells: Array<HeadCell> = [
  {
    id: 'deaNumber',
    numeric: false,
    disablePadding: false,
    width: '10%',
    label: 'DEA #',
  },
  {
    id: 'expirationDate',
    numeric: false,
    disablePadding: false,
    width: '10%',
    label: 'Expiration Date',
  },
  {
    id: 'deaName',
    numeric: false,
    disablePadding: false,
    width: '10%',
    label: 'Name',
  },
  {
    id: 'csaBusStreet1',
    numeric: false,
    disablePadding: false,
    width: '25%',
    label: 'Address',
  },
  {
    id: 'csaBusCity',
    numeric: false,
    disablePadding: false,
    width: '15%',
    label: 'City',
  },
  {
    id: 'csaBusStateCd',
    numeric: false,
    disablePadding: false,
    width: '9%',
    label: 'State',
  },
  {
    id: 'csaBusPostalCd',
    numeric: false,
    disablePadding: false,
    width: '21%',
    label: 'Zip',
  },
];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (property: keyof CsaFilterData) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;

  const createSortHandler = (property: keyof CsaFilterData) => () => {
    onRequestSort(property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            color="default"
            inputProps={{ 'aria-label': 'select all DEA numbers' }}
          />
        </TableCell>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            width={headCell.width}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}>
            <StyledTableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onPointerUp={createSortHandler(headCell.id)}
              onKeyUp={handleKey(createSortHandler(headCell.id))}
              tabIndex={0}>
              {headCell.label}
              {orderBy === headCell.id ? (
                <VisuallyHidden>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </VisuallyHidden>
              ) : null}
            </StyledTableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const Toolba = styled(Toolbar)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
}));
const HighlightToolba = styled(Toolbar)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  color: theme.palette.text.primary,
}));
const SearchText = styled('label')(({ theme }) => ({ marginRight: '1rem' }));

interface EnhancedTableToolbarProps {
  numSelected: number;
  onFitlerTextChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const { numSelected, onFitlerTextChange } = props;

  return numSelected > 0 ? (
    <HighlightToolba>
      <h4>Available DEA Registration Numbers to Request</h4>

      <SearchText htmlFor="filter-dea-number">Filter</SearchText>
      <TextField
        id="filter-dea-number"
        data-testid="filter-dea-number-testid"
        onChange={onFitlerTextChange}
        style={{ width: '40%' }}
        variant="outlined"
      />
    </HighlightToolba>
  ) : (
    <Toolba>
      <h4>Available DEA Registration Numbers to Request</h4>

      <SearchText htmlFor="filter-dea-number">Filter</SearchText>
      <TextField
        id="filter-dea-number"
        data-testid="filter-dea-number-testid"
        onChange={onFitlerTextChange}
        style={{ width: '40%' }}
        variant="outlined"
      />
    </Toolba>
  );
};

const Tabl = styled(Table)(({ theme }) => ({ minWidth: 350 }));
const TableCel = styled(TableCell)(({ theme }) => ({ align: 'left' }));
const VisuallyHidden = styled('span')(({ theme }) => ({
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  top: 20,
  width: 1,
}));
const Root = styled('div')(({ theme }) => ({
  width: '100%',
}));
const NoData = styled('p')(({ theme }) => ({
  margin: '0.2rem',
}));
const Pape = styled(Paper)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
}));

export function CsaFilterTable(props: {
  deaNums: string[];
  searchResult: SearchResp;
  save: DeaNumFunction;
  searchByPage: DeaPageFunction;
}) {
  const { searchResult, save, searchByPage } = props;
  const [isEmptyRow, setIsEmptyRow] = useState(true);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof CsaFilterData>('key');
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(CSA_FILTER_ROWS_PER_PAGE);
  const [tableData, setTableData] = useState<Array<CsaFilterData>>([]);
  const [tableDataPropCopy, setTableDataPropCopy] = useState<
    Array<CsaFilterData>
  >([]); // keep a copy for dynamic filter.

  useEffect(() => {
    formatResult(searchResult);
  }, [searchResult]);

  const formatResult = (searchResult: SearchResp) => {
    const { data, status } = searchResult;
    if (status === 200 && data && data.content && data.content.length > 0) {
      const { content, totalElements, number, size } = data;
      setPage(number);
      setRowsPerPage(size);
      setTotalElements(totalElements);
      setIsEmptyRow(false);
      const formattedData = content.map((item: CsaRawData) => {
        return { ...item, key: uuidv4() };
      });
      setTableData(formattedData);
      setTableDataPropCopy(formattedData);
    } else {
      // we need to seperate out error condition from no row found
      setIsEmptyRow(true);
      setTableData([]);
      setTableDataPropCopy([]);
    }
  };

  const handleRequestSort = (property: keyof CsaFilterData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      lcompose(
        setSelected,
        lmap<CsaFilterData, string>(pathOr('', 'deaNumber'))
      )(tableData);
    } else {
      lcompose(
        setSelected,
        lmap<CsaFilterData, string>(pathOr('', 'deaNumber')),
        lfilter<CsaFilterData>(
          lcompose(includes(props.deaNums), pathOr('', 'deaNumber'))
        )
      )(tableData);
    }
  };

  const handleClick = (deaNumber: string) => {
    setSelected(a =>
      a.indexOf(deaNumber) === -1
        ? [...a, deaNumber]
        : lfilter<string>(lnegate(leq(deaNumber)))(a)
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setSelected([]);

    setPage(newPage);
    const pageInfo: IPageInfo = {
      pageNumber: newPage,
      pageSize: rowsPerPage,
    };
    searchByPage(pageInfo);
  };

  const handleChangeRowsPerPage = lcompose(
    pageSize => {
      setPage(0);
      setRowsPerPage(pageSize);
      searchByPage({
        pageNumber: 0,
        pageSize,
      });
    },
    (a: string) => parseInt(a, 10),
    pathOr('10', 'target.value')
  );

  const onChangeFilterText = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event && event.target && event.target.value) || '';
    const filteredRows = tableDataPropCopy.filter((row: CsaFilterData) => {
      const filterArray: Array<string> = Object.keys(row);

      for (let i = 0; i < filterArray.length; i++) {
        const field =
          filterArray[i] === 'expirationDate'
            ? showExpDate(row)
            : (row as any)[filterArray[i]] || '';
        const strVal = field.toLowerCase();
        const searchString = value.toLowerCase();
        if (strVal.includes(searchString)) {
          return true;
        }
      }

      return false;
    });
    setTableData(filteredRows);
  };

  const handleAddSelected = () => {
    lcompose(
      lmap<IDeaEditMode, void>(save),
      lmap<string, IDeaEditMode>((deaNum: string) => ({
        key: uuidv4(),
        deaNum,
        editMode: 'Read',

        expDt: flow(
          lfind({ deaNumber: deaNum }),
          pathOr(null, 'expirationDate'),
          parseDate
        )(tableData),

        msgCode: '',
        msgText: '',
        serialNumber: null,
      }))
    )(selected);
  };

  const isSelected = includes(selected);

  const renderTable = () => {
    return (
      <>
        <FlexBox>
          <EnhancedTableToolbar
            numSelected={selected.length}
            onFitlerTextChange={onChangeFilterText}
          />
          <StyledTablePagination
            data-testid="table-paginator-top-testid"
            rowsPerPageOptions={CSA_FILTER_ROWS_PER_PAGE_OPTION}
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </FlexBox>

        <TableContainer>
          <Tabl
            aria-labelledby="tableTitle"
            size="small"
            aria-label="enhanced table">
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={tableData.length}
            />
            <TableBody>
              {stableSort(tableData as any, getComparator(order, orderBy)).map(
                (tableData, index) => {
                  const isItemSelected = isSelected(tableData.deaNumber);
                  const isItemAdded = props.deaNums.includes(
                    tableData.deaNumber as any
                  );

                  // TODO fix any 2 above, 1 below

                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={tableData.key}
                      selected={isItemSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected || isItemAdded}
                          disabled={isItemAdded}
                          onChange={() =>
                            handleClick(tableData.deaNumber as any)
                          }
                          color="default"
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>

                      <TableCel>{tableData.deaNumber}</TableCel>
                      <TableCel>{showExpDate(tableData)}</TableCel>
                      <TableCel>{tableData.deaName}</TableCel>
                      <TableCel>{tableData.csaBusStreet1}</TableCel>
                      <TableCel>{tableData.csaBusCity}</TableCel>
                      <TableCel>{tableData.csaBusStateCd}</TableCel>
                      <TableCel>{tableData.csaBusPostalCd}</TableCel>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Tabl>
        </TableContainer>
        <Box display="flex" p={1} bgcolor="background.paper">
          <Box p={1} flexGrow={1}>
            <Button
              variant="contained"
              color="primary"
              disabled={isEmpty(selected)}
              onClick={handleAddSelected}>
              Add to my request
            </Button>
          </Box>
          <Box p={0}>
            <StyledTablePagination
              data-testid="table-paginator-bottom-testid"
              rowsPerPageOptions={CSA_FILTER_ROWS_PER_PAGE_OPTION}
              count={totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>
        {/* <pre>{JSON.stringify(selected, null, 2)}</pre> */}
      </>
    );
  };
  if (isEmptyRow) {
    return (
      <Root>
        <NoData>
          No matching records found. Please change the filter criteria
        </NoData>
      </Root>
    );
  }
  return (
    <Root>
      <Pape>{renderTable()}</Pape>
    </Root>
  );
}
